import React from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { Typography, Tooltip, Box, Divider, Grid } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PercentIcon from '@mui/icons-material/Percent';
import PriceCheckRoundedIcon from '@mui/icons-material/PriceCheckRounded';
import RealEstateAgentRoundedIcon from '@mui/icons-material/RealEstateAgentRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import StyledCircularProgress from '../../../Components/Loading/StyledCircularProgress';
import { CenteredFlexBox } from '../../../Components/FlexBox';
import { createNewDate, culture, options } from '../../../Helpers/dates';
import AmountInfo from './DataDetailsBox/AmountInfo';
import ListInfo from './DataDetailsBox/ListInfo';

const useStyles = () => ({
  icnWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '4px',
  },
  infoStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  captionStyle: {
    fontSize: '14px',
    color: (theme) => theme.palette.grey.darker,
  },
  valueStyle: {
    fontSize: '20px',
  },
});

const ProductInfoWidget = function ProductInfoWidget({ data: product }) {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <Box
      style={{
        overflow: 'auto',
        padding: '16px 24px',
        display: 'flex',
        gap: '8px',
        flexDirection: 'column',
        textOverflow: 'ellipsis',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontSize: '14px',
          color: (theme) => theme.palette.grey.darker,
        }}
      >{`${intl.messages.PRACTICENUMBER}: ${
        product?.dossier || intl.messages.UNSPECIFIED
      }`}</Typography>
      <Typography
        variant="caption"
        sx={{
          fontSize: '14px',
          color: (theme) => theme.palette.grey.darker,
        }}
      >{`${intl.messages.START_END_DATE}: ${
        product?.startDate !== null && product?.endDate !== null
          ? `${createNewDate(product?.startDate).toLocaleDateString(
              culture,
              options
            )} - ${createNewDate(product?.endDate).toLocaleDateString(
              culture,
              options
            )}`
          : ''
      }`}</Typography>
      <div style={classes.icnWrap}>
        <Tooltip
          title={
            product?.isActive ? intl.messages.ACTIVE : intl.messages.INACTIVE
          }
          placement="bottom"
        >
          <FiberManualRecordIcon
            sx={{
              color: (theme) =>
                product?.isActive
                  ? theme.palette.graphs.answered
                  : theme.palette.graphs.notAnswered,
            }}
          />
        </Tooltip>
        <Typography
          noWrap
          variant="h6"
          sx={{
            fontSize: '24px',
          }}
        >
          {product?.productName || intl.messages.UNAVAILABLE}
        </Typography>
      </div>
      <CenteredFlexBox gap="8px">
        <ListInfo
          icon={<RealEstateAgentRoundedIcon />}
          width="50%"
          title={`${intl.messages.HAS_MORTGAGE}:`}
          value={
            product?.hasMortgage === true
              ? intl.messages.YES
              : product?.hasMortgage === false
                ? intl.messages.NO
                : '-'
          }
        />
        <ListInfo
          icon={<SavingsRoundedIcon />}
          width="50%"
          title={`${intl.messages.IS_SALARY_BACKED_LOAN}:`}
          value={
            product?.isSalaryBackedLoan === true
              ? intl.messages.YES
              : product?.isSalaryBackedLoan === false
                ? intl.messages.NO
                : '-'
          }
        />
      </CenteredFlexBox>
      <ListInfo
        icon={<PriceCheckRoundedIcon />}
        title={`${intl.messages.GOOD_TYPE}: `}
        value={product?.otherInfo || intl.messages.UNSPECIFIED}
      />
      <ListInfo
        icon={<CampaignIcon />}
        title={`${intl.messages.CHANNEL}: `}
        value={product?.origin || intl.messages.UNSPECIFIED}
      />
      <ListInfo
        icon={<InsertDriveFileIcon />}
        title={intl.messages.PRACTICESTATUS}
        value={product?.productStatus || intl.messages.UNAVAILABLE}
      />
      <ListInfo
        icon={<PercentIcon />}
        title={intl.messages.PRACTICE_RATE}
        value={product?.rate || intl.messages.UNAVAILABLE}
      />
      <Divider variant="middle" />
      <Grid container spacing={2} sx={{
        mt: "8px"
      }}>
        <Grid size={{ lg: 4, md: 6 }} sx={{
          order: { lg: 1, md: 2 }
        }}>
          <AmountInfo
            title={intl.messages.PAID_AMOUNT}
            value={
              product?.paidImport || product?.amount - product?.remainingAmount
            }
            alignment="flex-start"
          />
          <AmountInfo
            title={intl.messages.RESIDUAL_AMOUNT}
            value={product?.remainingAmount}
            alignment="flex-start"
          />
        </Grid>
        <Grid size={{ lg: 4, md: 12 }} sx={{
          order: { lg: 2, md: 1 }
        }}>
          <StyledCircularProgress
            progressData={{
              value: product?.paidInstallments,
              total: product?.installments,
            }}
            label={0}
            color="#49A600"
            size="100px"
          />
        </Grid>
        <Grid size={{ lg: 4, md: 6 }} sx={{
          order: { lg: 3, md: 3 }
        }}>
          <AmountInfo
            title={intl.messages.TOTAL_AMOUNT}
            value={product?.amount}
            alignment="flex-end"
          />
          <AmountInfo
            title={intl.messages.INSTALLMENT_AMOUNT}
            value={product?.installmentAmount}
            alignment="flex-end"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

ProductInfoWidget.propTypes = {
  data: PropTypes.object.isRequired,
};
export default ProductInfoWidget;
